import {csrfToken,type StripeTestInvoiceState,type StripeTestQuoteReadback} from './api';

export type CheckoutScope = {identityId:string;organizationId:string};
export type CheckoutAttempt = {
  identityId:string;organizationId:string;orderId:string;quoteId:string;
  orderVersion:number;quoteVersion:number;action:'approve'|'create';key:string;
};
export type CheckoutResult = {intent_id:string;order_id:string;amount_minor:number;currency:'USD';mode:'test';state:StripeTestInvoiceState;checkout_url?:string};
const states = new Set(['reserved','creation_unknown','open','paid_held','settled','expired','failed']);
const integer = (value:unknown):value is number => Number.isSafeInteger(value) && (value as number)>0;
const record = (value:unknown):value is Record<string,unknown> => typeof value==='object' && value!==null && !Array.isArray(value);
const refuse = ():never => {throw new Error('The TEST checkout request is unavailable.');};
function assertIdentifier(value:string):void {
  if(!/^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value))refuse();
}
const attemptKeys = ['action','identityId','key','orderId','orderVersion','organizationId','quoteId','quoteVersion'].sort().join(',');

function storageKey(scope:CheckoutScope,orderId:string,action:CheckoutAttempt['action']):string {
  [scope.identityId,scope.organizationId,orderId].forEach(assertIdentifier);
  return `profenz.stripe-test.request.v1:${scope.organizationId}:${scope.identityId}:${orderId}:${action}`;
}

/** Client request keys only; never a provider secret, hosted URL, or authorization source. */
export function retainedAttempt(scope:CheckoutScope,orderId:string,action:CheckoutAttempt['action']):CheckoutAttempt|null {
  try {
    const raw=sessionStorage.getItem(storageKey(scope,orderId,action));
    if(raw===null)return null;
    const value:unknown=JSON.parse(raw);
    if(!record(value)||Object.keys(value).sort().join(',')!==attemptKeys||value.identityId!==scope.identityId
      ||value.organizationId!==scope.organizationId||value.orderId!==orderId||value.action!==action
      ||!integer(value.orderVersion)||!integer(value.quoteVersion)||typeof value.quoteId!=='string'||typeof value.key!=='string')return refuse();
    assertIdentifier(value.quoteId);assertIdentifier(value.key);
    return value as CheckoutAttempt;
  }catch{return refuse();}
}

export function attemptMatches(attempt:CheckoutAttempt,quote:StripeTestQuoteReadback):boolean {
  return attempt.orderId===quote.order_id&&attempt.quoteId===quote.quote_id
    &&attempt.orderVersion===quote.order_version&&attempt.quoteVersion===quote.quote_version;
}

/** Persist and verify before the first mutation. Existing mismatched/corrupt requests never get a new key. */
export function prepareAttempt(scope:CheckoutScope,quote:StripeTestQuoteReadback,action:CheckoutAttempt['action']):CheckoutAttempt {
  const old=retainedAttempt(scope,quote.order_id,action);
  if(old){if(!attemptMatches(old,quote))return refuse();return old;}
  if(!integer(quote.order_version)||!integer(quote.quote_version))return refuse();
  assertIdentifier(quote.quote_id);
  const attempt:CheckoutAttempt={...scope,orderId:quote.order_id,quoteId:quote.quote_id,
    orderVersion:quote.order_version,quoteVersion:quote.quote_version,action,key:crypto.randomUUID()};
  try {
    sessionStorage.setItem(storageKey(scope,quote.order_id,action),JSON.stringify(attempt));
    const saved=retainedAttempt(scope,quote.order_id,action);
    if(!saved||JSON.stringify(saved)!==JSON.stringify(attempt))return refuse();
    return saved;
  }catch{return refuse();}
}

export function stripeHostedUrl(value:unknown):string {
  if(typeof value!=='string'||value.length>4096||/[\x00-\x20\x7f\\]/.test(value)
    ||!value.startsWith('https://checkout.stripe.com/c/pay/'))return refuse();
  const url=new URL(value);
  if(url.protocol!=='https:'||url.hostname!=='checkout.stripe.com'||url.port||url.username||url.password
    ||!/^\/c\/pay\/cs_test_[A-Za-z0-9]+$/.test(url.pathname))return refuse();
  return value;
}

async function post(path:string,body:Record<string,string|number>,signal?:AbortSignal):Promise<Record<string,unknown>> {
  try {
    const csrf=csrfToken();if(!csrf)return refuse();
    const response=await fetch(path,{method:'POST',credentials:'same-origin',cache:'no-store',signal,
      headers:{Accept:'application/json','Content-Type':'application/json','X-ProFenz-CSRF':csrf},body:JSON.stringify(body)});
    if(!response.ok)return refuse();
    const value:unknown=await response.json();if(!record(value))return refuse();return value;
  }catch{return refuse();}
}

function checkout(value:unknown,orderId:string,amount:number,intentId?:string):CheckoutResult {
  if(!record(value)||typeof value.intent_id!=='string'||value.order_id!==orderId||value.amount_minor!==amount
    ||!integer(value.amount_minor)||value.currency!=='USD'||value.mode!=='test'||!states.has(String(value.state))
    ||(intentId!==undefined&&value.intent_id!==intentId))return refuse();
  assertIdentifier(value.intent_id);
  if(value.checkout_url!==undefined){if(value.state!=='open')return refuse();stripeHostedUrl(value.checkout_url);}
  // Pick safe fields: never propagate unrecognized provider data from a response.
  return {intent_id:value.intent_id,order_id:orderId,amount_minor:amount,currency:'USD',mode:'test',
    state:value.state as StripeTestInvoiceState,...(typeof value.checkout_url==='string'?{checkout_url:value.checkout_url}:{})};
}

export async function approveStripeTestQuote(attempt:CheckoutAttempt,quote:StripeTestQuoteReadback,signal?:AbortSignal):Promise<StripeTestQuoteReadback> {
  if(attempt.action!=='approve'||!attemptMatches(attempt,quote)||quote.state!=='confirmed')return refuse();
  const body=await post(`/api/v1/customer/orders/${attempt.orderId}/stripe-test/quotes/${attempt.quoteId}/approve`,
    {expected_quote_version:attempt.quoteVersion,idempotency_key:attempt.key},signal);
  const q=body.quote;
  if(!record(q)||typeof q.quote_id!=='string'||q.quote_id===quote.quote_id||q.quote_version!==quote.quote_version+1
    ||q.order_id!==quote.order_id||q.order_version!==quote.order_version||q.amount_minor!==quote.amount_minor
    ||q.currency!=='USD'||q.mode!=='test'||q.state!=='approved')return refuse();
  assertIdentifier(q.quote_id);
  return {...quote,quote_id:q.quote_id,quote_version:q.quote_version as number,state:'approved'};
}

export async function createStripeTestCheckout(attempt:CheckoutAttempt,quote:StripeTestQuoteReadback,recover:boolean,signal?:AbortSignal,expectedIntentId?:string):Promise<CheckoutResult> {
  if(attempt.action!=='create'||!attemptMatches(attempt,quote)||quote.state!=='approved')return refuse();
  const body=await post(`/api/v1/customer/orders/${attempt.orderId}/stripe-test/quotes/${attempt.quoteId}/${recover?'recover':'create'}`,
    {expected_order_version:attempt.orderVersion,expected_quote_version:attempt.quoteVersion,idempotency_key:attempt.key},signal);
  return checkout(body.checkout,quote.order_id,quote.amount_minor,expectedIntentId);
}

export async function resumeStripeTestCheckout(orderId:string,intentId:string,amount:number,signal?:AbortSignal):Promise<CheckoutResult> {
  [orderId,intentId].forEach(assertIdentifier);
  const body=await post(`/api/v1/customer/orders/${orderId}/stripe-test/intents/${intentId}/resume`,{},signal);
  const result=checkout(body.checkout,orderId,amount,intentId);
  if(result.state!=='open'||!result.checkout_url)return refuse();
  return result;
}
